import React, { useState, useEffect, useRef } from 'react';
import {
  getHazirCevaplar,
  addHazirCevap,
  updateHazirCevap,
  deleteHazirCevap,
  getHazirCevapImageUrl
} from '../../services/requestService';
import Spinner from '../common/Spinner';
import useModalClose from '../../hooks/useModalClose';

const EMPTY_FORM = { id: null, cevap: '', resim: null };
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const HazirCevaplarModal = ({ isOpen, onClose, category }) => {
  const [cevaplar, setCevaplar] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setPreviewImage(null);
    setFormError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const fetchCevaplar = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setCevaplar(await getHazirCevaplar(category.id));
    } catch (err) {
      console.error('Hazır cevaplar yüklenemedi:', err);
      setLoadError('Hazır cevaplar yüklenemedi.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && category) {
      fetchCevaplar();
    } else {
      setCevaplar([]);
      setLoadError(null);
      resetForm();
    }
  }, [isOpen, category]);

  const handleEdit = (item) => {
    resetForm();
    setForm({ id: item.id, cevap: item.cevap || '', resim: null });
    setPreviewImage(getHazirCevapImageUrl(item.resim));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      setFormError('Resim boyutu 5MB\'dan büyük olamaz.');
      e.target.value = '';
      return;
    }
    setFormError(null);
    setForm(prev => ({ ...prev, resim: file }));
    setPreviewImage(URL.createObjectURL(file));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.cevap.trim()) {
      setFormError('Cevap metni boş olamaz.');
      return;
    }
    setIsSaving(true);
    setFormError(null);
    try {
      const payload = { cevap: form.cevap.trim(), tipId: category.id, resim: form.resim };
      if (form.id) {
        await updateHazirCevap(form.id, payload);
      } else {
        await addHazirCevap(payload);
      }
      resetForm();
      await fetchCevaplar();
    } catch (err) {
      console.error('Hazır cevap kaydedilemedi:', err);
      setFormError('Hazır cevap kaydedilemedi.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Bu hazır cevabı silmek istediğinize emin misiniz?')) return;
    setDeletingId(item.id);
    try {
      await deleteHazirCevap(item.id);
      if (form.id === item.id) resetForm();
      await fetchCevaplar();
    } catch (err) {
      console.error('Hazır cevap silinemedi:', err);
      setLoadError('Hazır cevap silinemedi.');
    } finally {
      setDeletingId(null);
    }
  };

  useModalClose(isOpen, onClose);

  if (!isOpen || !category) return null;

  const renderList = () => {
    if (isLoading) {
      return (
        <div className="hc-state">
          <Spinner size="small" />
          <span>Yükleniyor...</span>
        </div>
      );
    }
    if (loadError) {
      return (
        <div className="hc-state hc-state--error">
          <span>{loadError}</span>
          <button type="button" className="hc-link-button" onClick={fetchCevaplar}>Tekrar dene</button>
        </div>
      );
    }
    if (cevaplar.length === 0) {
      return <div className="hc-state">Henüz hazır cevap eklenmemiş.</div>;
    }
    return (
      <ul className="hc-list">
        {cevaplar.map((item) => (
          <li key={item.id} className={`hc-item ${form.id === item.id ? 'hc-item--editing' : ''}`}>
            {item.resim && (
              <img className="hc-item__image" src={getHazirCevapImageUrl(item.resim)} alt="" />
            )}
            <p className="hc-item__text">{item.cevap}</p>
            <div className="hc-item__actions">
              <button type="button" className="hc-link-button" onClick={() => handleEdit(item)}>
                Düzenle
              </button>
              <button
                type="button"
                className="hc-link-button hc-link-button--danger"
                onClick={() => handleDelete(item)}
                disabled={deletingId === item.id}
              >
                {deletingId === item.id ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content hazir-cevaplar-modal">
        <div className="hc-header">
          <div>
            <h3>Hazır Cevaplar</h3>
            <span className="hc-subtitle">{category.adi}</span>
          </div>
          <button type="button" className="hc-close" onClick={onClose} aria-label="Kapat">×</button>
        </div>

        <div className="hc-body">
          {renderList()}

          {!loadError && (
            <form className="hc-form" onSubmit={handleSave}>
              <h4>{form.id ? 'Hazır cevabı düzenle' : 'Yeni hazır cevap'}</h4>
              <textarea
                rows={4}
                maxLength={2000}
                value={form.cevap}
                onChange={(e) => setForm(prev => ({ ...prev, cevap: e.target.value }))}
                placeholder="Vatandaşa gönderilecek cevap metni"
              />
              <div className="hc-form__image">
                <label className="hc-file-button">
                  {previewImage ? 'Resmi değiştir' : 'Resim ekle (isteğe bağlı)'}
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} hidden />
                </label>
                {previewImage && <img src={previewImage} alt="Önizleme" />}
              </div>
              {formError && <div className="hc-form__error">{formError}</div>}
              <div className="hc-form__actions">
                {form.id && (
                  <button type="button" className="hc-button hc-button--secondary" onClick={resetForm} disabled={isSaving}>
                    Vazgeç
                  </button>
                )}
                <button type="submit" className="hc-button hc-button--primary" disabled={isSaving}>
                  {isSaving ? 'Kaydediliyor...' : form.id ? 'Güncelle' : 'Ekle'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default HazirCevaplarModal;
